import React, { useState, useEffect, useRef } from "react";
import {
  Plus,
  Search,
  Copy,
  Check,
  ExternalLink,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Loader2,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import {
  customerService,
  Customer,
  HubSpotCompany,
  DealProcessingResult,
} from "../../services/CustomerService";
import { useToastContext } from "../../context/ToastContext";

interface ImportResult {
  company: HubSpotCompany;
  success: boolean;
  customer?: Customer;
  dealProcessing?: DealProcessingResult | null;
  error?: string;
}

const Customers: React.FC = () => {
  const { showToast } = useToastContext();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [companies, setCompanies] = useState<HubSpotCompany[]>([]);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [companiesConfigMissing, setCompaniesConfigMissing] = useState(false);
  const [companiesPagination, setCompaniesPagination] = useState<{
    hasNextPage: boolean;
    nextAfter?: string;
  }>({
    hasNextPage: false,
    nextAfter: undefined,
  });
  const [isLoadingMoreCompanies, setIsLoadingMoreCompanies] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(
    new Set(),
  );
  const MAX_SELECTIONS = 5;
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{
    current: number;
    total: number;
    companyName: string;
  } | null>(null);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [isItemsPerPageOpen, setIsItemsPerPageOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const [statusFilter, setStatusFilter] = useState<
    "active" | "pending" | "inactive" | "all"
  >("all");
  const slideOverRef = useRef<HTMLDivElement>(null);
  const itemsPerPageRef = useRef<HTMLDivElement>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Load customers
  useEffect(() => {
    loadCustomers();
  }, [currentPage, itemsPerPage, searchQuery, statusFilter]);

  // Load default curated list when modal opens
  useEffect(() => {
    if (isAddCustomerOpen && !showResults) {
      loadDefaultCompanies();
    }
  }, [isAddCustomerOpen, showResults]);

  // Debounced search - when user types, replace default list with search results
  useEffect(() => {
    if (!isAddCustomerOpen) return;

    if (customerSearch.length === 0) {
      // If search cleared, reload default list
      loadDefaultCompanies();
      return;
    }

    if (customerSearch.length < 2) {
      // Don't search yet, but keep default list
      return;
    }

    const timer = setTimeout(() => {
      searchCompanies();
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearch, isAddCustomerOpen]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      const response = await customerService.getCustomers(params);
      setCustomers(response.data.customers);
      setPagination(response.data.pagination);
    } catch (error: any) {
      console.error("Error loading customers:", error);
      showToast("error", error.message || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultCompanies = async () => {
    setSearchingCompanies(true);
    try {
      // Initial load: limit 20, no search (cursor pagination)
      const response = await customerService.searchHubSpotCompanies({
        limit: 20,
      });
      setCompanies(response.data.companies);
      setCompaniesConfigMissing(!!response.data.configMissing);
      // Handle configMissing: true (show empty state, don't treat as error)
      if (response.data.configMissing) {
        setCompanies([]);
      }
      setCompaniesPagination({
        hasNextPage: response.data.pagination.hasNextPage || false,
        nextAfter: response.data.pagination.nextAfter,
      });
    } catch (error: any) {
      console.error("Error loading default companies:", error);
      // Don't show toast for configMissing (it's expected, not an error)
      if (
        !error.message?.includes("configMissing") &&
        error.response?.status !== 200
      ) {
        showToast("error", error.message || "Failed to load companies");
      }
      setCompanies([]);
      setCompaniesConfigMissing(false);
      setCompaniesPagination({
        hasNextPage: false,
        nextAfter: undefined,
      });
    } finally {
      setSearchingCompanies(false);
    }
  };

  const searchCompanies = async () => {
    // Enforce search length ≥ 2 chars before sending request
    if (customerSearch.length < 2) {
      return;
    }

    setSearchingCompanies(true);
    try {
      const response = await customerService.searchHubSpotCompanies({
        search: customerSearch,
        hasDeals: true,
        limit: 50,
      });
      setCompanies(response.data.companies);
      setCompaniesConfigMissing(!!response.data.configMissing);
      // Handle configMissing: true (show empty state, don't treat as error)
      if (response.data.configMissing) {
        setCompanies([]);
      }
      setCompaniesPagination({
        hasNextPage: response.data.pagination.hasNextPage || false,
        nextAfter: response.data.pagination.nextAfter,
      });
    } catch (error: any) {
      console.error("Error searching HubSpot companies:", error);
      // Don't show toast for configMissing (it's expected, not an error)
      // Handle 400 error for search < 2 chars
      if (error.response?.status === 400) {
        // Validation error - don't show toast, just clear results
        setCompanies([]);
        setCompaniesConfigMissing(false);
        setCompaniesPagination({
          hasNextPage: false,
          nextAfter: undefined,
        });
      } else if (
        !error.message?.includes("configMissing") &&
        error.response?.status !== 200
      ) {
        showToast(
          "error",
          error.message || "Failed to search HubSpot companies",
        );
        setCompanies([]);
        setCompaniesConfigMissing(false);
        setCompaniesPagination({
          hasNextPage: false,
          nextAfter: undefined,
        });
      }
    } finally {
      setSearchingCompanies(false);
    }
  };

  const loadMoreCompanies = async () => {
    if (!companiesPagination.hasNextPage || isLoadingMoreCompanies) {
      return;
    }

    setIsLoadingMoreCompanies(true);
    try {
      // Use limit + after (cursor pagination)
      const response = await customerService.searchHubSpotCompanies({
        search: customerSearch.length >= 2 ? customerSearch : undefined,
        hasDeals: customerSearch.length >= 2 ? true : undefined,
        limit: customerSearch.length >= 2 ? 50 : 20,
        after: companiesPagination.nextAfter,
      });
      // Append new companies to existing list
      setCompanies((prev) => [...prev, ...response.data.companies]);
      setCompaniesConfigMissing(!!response.data.configMissing);
      setCompaniesPagination({
        hasNextPage: response.data.pagination.hasNextPage || false,
        nextAfter: response.data.pagination.nextAfter,
      });
    } catch (error: any) {
      console.error("Error loading more companies:", error);
      showToast("error", error.message || "Failed to load more companies");
    } finally {
      setIsLoadingMoreCompanies(false);
    }
  };

  const handleImport = async () => {
    if (selectedCompanyIds.size === 0) {
      showToast("info", "Please select at least one company to import");
      return;
    }

    setImporting(true);
    setImportResults([]);
    setShowResults(false);

    const selected = Array.from(selectedCompanyIds);
    const results: ImportResult[] = [];

    for (let i = 0; i < selected.length; i++) {
      const companyId = selected[i];
      const company = companies.find((c) => c.id === companyId);
      if (!company) continue;

      setImportProgress({
        current: i + 1,
        total: selected.length,
        companyName: company.name,
      });

      try {
        // Generate email from domain or name (per documentation)
        const email = company.domain
          ? `billing@${company.domain}`
          : `${company.name.toLowerCase().replace(/\s+/g, ".")}@example.com`;

        const response = await customerService.createCustomer({
          name: company.name,
          email: email,
          hubspotCompanyId: company.id,
          status: "active",
          metadata: {
            domain: company.domain,
          },
        });

        results.push({
          company,
          success: true,
          customer: response.data.customer,
          dealProcessing: response.data.dealProcessing,
        });
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Unknown error occurred";

        // Handle duplicate errors gracefully
        if (error?.response?.status === 409) {
          results.push({
            company,
            success: false,
            error: "Customer already exists with this email or linked ID",
          });
        } else {
          results.push({
            company,
            success: false,
            error: errorMessage,
          });
        }
      }
    }

    setImportProgress(null);
    setImportResults(results);
    setShowResults(true);
    setSelectedCompanyIds(new Set());
    setImporting(false);

    // Refresh customer list
    await loadCustomers();

    // Show summary toast
    const successCount = results.filter((r) => r.success).length;
    const failCount = results.filter((r) => !r.success).length;
    if (successCount > 0) {
      showToast(
        "success",
        `Successfully imported ${successCount} customer${
          successCount !== 1 ? "s" : ""
        }${failCount > 0 ? ` (${failCount} failed)` : ""}`,
      );
    } else {
      showToast(
        "error",
        `Failed to import customers: ${failCount} error${
          failCount !== 1 ? "s" : ""
        }`,
      );
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-light dark:bg-green-dark/20 text-green-dark dark:text-green-dark";
      case "pending":
        return "bg-secondary/10 text-secondary";
      case "inactive":
        return "bg-background-panel dark:bg-background-panelDark text-text-secondary dark:text-text-secondaryDark";
      default:
        return "bg-background-panel dark:bg-background-panelDark text-text-secondary dark:text-text-secondaryDark";
    }
  };

  const formatHubSpotId = (id?: string) => {
    if (!id) return "-";
    return `hs_${id}`;
  };

  const formatStripeId = (id?: string) => {
    if (!id) return "-";
    return id.startsWith("cus_") ? id : `cus_${id}`;
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      showToast("success", "Copied to clipboard");
    } catch (error) {
      showToast("error", "Failed to copy");
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLimitChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        itemsPerPageRef.current &&
        !itemsPerPageRef.current.contains(event.target as Node)
      ) {
        setIsItemsPerPageOpen(false);
      }
      if (
        statusFilterRef.current &&
        !statusFilterRef.current.contains(event.target as Node)
      ) {
        setIsStatusFilterOpen(false);
      }
    };

    if (isItemsPerPageOpen || isStatusFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isItemsPerPageOpen, isStatusFilterOpen]);

  const handleOpenAddCustomer = () => {
    setIsClosing(false);
    setIsOpening(true);
    setIsAddCustomerOpen(true);
    setShowResults(false);
    setImportResults([]);
    setSelectedCompanyIds(new Set());
    setCustomerSearch("");
    // Set loading state immediately - will load default list
    setSearchingCompanies(true);
    setCompanies([]);
    setTimeout(() => {
      setIsOpening(false);
    }, 10);
  };

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsAddCustomerOpen(false);
      setIsClosing(false);
      setCustomerSearch("");
      setSelectedCompanyIds(new Set());
      setShowResults(false);
      setImportResults([]);
      setImportProgress(null);
    }, 150);
  };

  const handleToggleCompany = (companyId: string) => {
    setSelectedCompanyIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        // Check max selections limit
        if (newSet.size >= MAX_SELECTIONS) {
          showToast(
            "info",
            `Maximum ${MAX_SELECTIONS} companies can be selected`,
          );
          return prev;
        }
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  const handleSelectAllVisible = () => {
    const availableCompanies = companies.filter((c) => !c.alreadyInSystem);
    const allSelected = availableCompanies.every((c) =>
      selectedCompanyIds.has(c.id),
    );

    if (allSelected) {
      setSelectedCompanyIds(new Set());
    } else {
      setSelectedCompanyIds(new Set(availableCompanies.map((c) => c.id)));
    }
  };

  const availableCompanies = companies.filter((c) => !c.alreadyInSystem);
  const selectedCount = selectedCompanyIds.size;
  const allSelected =
    availableCompanies.length > 0 &&
    selectedCount === availableCompanies.length;

  return (
    <>
      <div className="w-full h-full bg-background-default dark:bg-background-dark flex flex-col overflow-hidden">
        <div className="max-w-[1600px] mx-auto w-full px-8 py-6 flex flex-col h-full min-h-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-shrink-0">
            <div>
              <h1 className="text-3xl font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                Customers
              </h1>
              <p className="text-base text-text-secondary dark:text-text-secondaryDark">
                Manage your unified customer records with Global IDs
              </p>
            </div>
            <button
              onClick={handleOpenAddCustomer}
              className="flex items-center gap-2 px-4 py-2.5 bg-black hover:bg-black/90 dark:bg-secondary dark:hover:bg-secondary-hover text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Customer
            </button>
          </div>

          {/* Search Bar and Filters */}
          <div className="mb-6 flex-shrink-0 space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, GID, or email..."
                className="w-full pl-11 pr-4 py-2.5 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition-colors"
              />
            </div>

            {/* Status Filter */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative" ref={statusFilterRef}>
                <button
                  onClick={() =>
                    customers.length > 0 &&
                    setIsStatusFilterOpen(!isStatusFilterOpen)
                  }
                  disabled={customers.length === 0}
                  className="px-3 py-1.5 text-sm font-medium bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-md text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-text-secondary dark:hover:border-text-secondaryDark transition-colors flex items-center gap-2 min-w-[120px] justify-between disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-background-paper dark:disabled:hover:bg-background-paperDark disabled:hover:border-divider dark:disabled:hover:border-divider-dark"
                >
                  <span>
                    {statusFilter === "all"
                      ? "All Status"
                      : statusFilter.charAt(0).toUpperCase() +
                        statusFilter.slice(1)}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isStatusFilterOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Dropdown menu */}
                {isStatusFilterOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-md shadow-lg z-50 min-w-[120px] overflow-hidden">
                    {[
                      { value: "all", label: "All Status" },
                      { value: "active", label: "Active" },
                      { value: "pending", label: "Pending" },
                      { value: "inactive", label: "Inactive" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setStatusFilter(option.value as any);
                          setIsStatusFilterOpen(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          statusFilter === option.value
                            ? "bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary font-medium"
                            : "text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Customers Table */}
          <div className="bg-background-paper dark:bg-background-paperDark rounded-xl border border-divider dark:border-divider-dark overflow-hidden flex-1 flex flex-col min-h-0 shadow-sm">
            {loading && customers.length === 0 ? (
              <div className="flex items-center justify-center h-full min-h-[320px]">
                <Loader2 className="w-8 h-8 animate-spin text-text-secondary dark:text-text-secondaryDark" />
              </div>
            ) : (
              <div className="overflow-auto flex-1 pb-2">
                <table className="w-full table-auto border-collapse">
                  <thead className="sticky top-0 z-10 bg-background-panel dark:bg-background-panelDark">
                    <tr className="border-b border-divider dark:border-divider-dark">
                      <th className="text-left py-4 px-6 text-sm font-semibold text-text-primary dark:text-text-primaryDark tracking-tight">
                        Customer Name
                      </th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-text-primary dark:text-text-primaryDark tracking-tight">
                        Global ID (GID)
                      </th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-text-primary dark:text-text-primaryDark tracking-tight">
                        HubSpot ID
                      </th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-text-primary dark:text-text-primaryDark tracking-tight">
                        Stripe ID
                      </th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-text-primary dark:text-text-primaryDark tracking-tight">
                        Product ID
                      </th>
                      <th className="text-left py-4 px-6 text-sm font-semibold text-text-primary dark:text-text-primaryDark tracking-tight">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-16 text-center">
                          <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                            No customers found
                          </p>
                        </td>
                      </tr>
                    ) : (
                      customers.map((customer) => (
                        <tr
                          key={customer.id}
                          className="border-b border-divider/60 dark:border-divider-dark/60 hover:bg-background-panel/80 dark:hover:bg-background-panelDark/80 transition-colors"
                        >
                          {/* Customer Name */}
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-blue-dark/10 dark:bg-blue-dark/20 flex items-center justify-center text-blue-dark dark:text-blue-dark font-semibold text-sm flex-shrink-0">
                                {getInitials(customer.name)}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-text-primary dark:text-text-primaryDark truncate">
                                  {customer.name}
                                </div>
                                <div className="text-xs text-text-secondary dark:text-text-secondaryDark truncate mt-0.5">
                                  {customer.email}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Global ID */}
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    customer.globalId,
                                    `global-${customer.id}`,
                                  )
                                }
                                className="text-sm font-mono text-text-primary dark:text-text-primaryDark hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer text-left"
                              >
                                {customer.globalId}
                              </button>
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    customer.globalId,
                                    `global-${customer.id}`,
                                  )
                                }
                                className="p-1 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors flex-shrink-0"
                                aria-label="Copy Global ID"
                              >
                                {copiedId === `global-${customer.id}` ? (
                                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark" />
                                )}
                              </button>
                            </div>
                          </td>

                          {/* HubSpot ID */}
                          <td className="py-4 px-6">
                            {customer.hubspotCompanyId ? (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    copyToClipboard(
                                      customer.hubspotCompanyId!,
                                      `hubspot-${customer.id}`,
                                    )
                                  }
                                  className="px-2.5 py-1 bg-secondary/10 text-secondary rounded-full text-xs font-medium truncate hover:opacity-80 transition-opacity cursor-pointer"
                                >
                                  {formatHubSpotId(customer.hubspotCompanyId)}
                                </button>
                                <a
                                  href={`https://app.hubspot.com/contacts/${customer.hubspotCompanyId}/company/${customer.hubspotCompanyId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors flex-shrink-0"
                                  aria-label="Open in HubSpot"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ExternalLink className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-secondary" />
                                </a>
                                <button
                                  onClick={() =>
                                    copyToClipboard(
                                      customer.hubspotCompanyId!,
                                      `hubspot-${customer.id}`,
                                    )
                                  }
                                  className="p-1 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors flex-shrink-0"
                                  aria-label="Copy HubSpot ID"
                                >
                                  {copiedId === `hubspot-${customer.id}` ? (
                                    <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  ) : (
                                    <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark" />
                                  )}
                                </button>
                              </div>
                            ) : (
                              <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                                —
                              </span>
                            )}
                          </td>

                          {/* Stripe ID */}
                          <td className="py-4 px-6">
                            {customer.stripeCustomerId ? (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    copyToClipboard(
                                      customer.stripeCustomerId!,
                                      `stripe-${customer.id}`,
                                    )
                                  }
                                  className="px-2.5 py-1 rounded-full text-xs font-medium truncate hover:opacity-80 transition-opacity cursor-pointer"
                                  style={{
                                    backgroundColor: "rgba(139, 92, 246, 0.1)",
                                    color: "#8B5CF6",
                                  }}
                                >
                                  {formatStripeId(customer.stripeCustomerId)}
                                </button>
                                <a
                                  href={`https://dashboard.stripe.com/customers/${customer.stripeCustomerId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors group flex-shrink-0"
                                  aria-label="Open in Stripe"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ExternalLink className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark group-hover:text-[#8B5CF6] transition-colors" />
                                </a>
                                <button
                                  onClick={() =>
                                    copyToClipboard(
                                      customer.stripeCustomerId!,
                                      `stripe-${customer.id}`,
                                    )
                                  }
                                  className="p-1 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors flex-shrink-0"
                                  aria-label="Copy Stripe ID"
                                >
                                  {copiedId === `stripe-${customer.id}` ? (
                                    <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  ) : (
                                    <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark" />
                                  )}
                                </button>
                              </div>
                            ) : (
                              <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                                —
                              </span>
                            )}
                          </td>

                          {/* Product ID */}
                          <td className="py-4 px-6">
                            {customer.productUserId ? (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    copyToClipboard(
                                      customer.productUserId!,
                                      `product-${customer.id}`,
                                    )
                                  }
                                  className="px-2.5 py-1 bg-green-light dark:bg-green-dark/20 text-green-dark dark:text-green-dark rounded-full text-xs font-medium hover:opacity-80 transition-opacity cursor-pointer"
                                >
                                  {customer.productUserId}
                                </button>
                                <button
                                  onClick={() =>
                                    copyToClipboard(
                                      customer.productUserId!,
                                      `product-${customer.id}`,
                                    )
                                  }
                                  className="p-1 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors flex-shrink-0"
                                  aria-label="Copy Product ID"
                                >
                                  {copiedId === `product-${customer.id}` ? (
                                    <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  ) : (
                                    <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark" />
                                  )}
                                </button>
                              </div>
                            ) : (
                              <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                                —
                              </span>
                            )}
                          </td>

                          {/* Status */}
                          <td className="py-4 px-6">
                            <span
                              className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(
                                customer.status,
                              )}`}
                            >
                              {customer.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer with Pagination Controls - Fixed to bottom of viewport */}
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background-default dark:bg-background-dark border-t border-divider dark:border-divider-dark">
          <div className="max-w-[1600px] mx-auto px-8">
            <div className="flex items-center justify-between gap-4 py-3">
              {/* Left side - Items per page */}
              <div className="relative" ref={itemsPerPageRef}>
                <button
                  onClick={() =>
                    pagination.totalCount > 0 &&
                    setIsItemsPerPageOpen(!isItemsPerPageOpen)
                  }
                  disabled={pagination.totalCount === 0}
                  className="px-3 py-1.5 text-sm font-medium bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-md text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-text-secondary dark:hover:border-text-secondaryDark transition-colors flex items-center gap-2 min-w-[120px] justify-between disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-background-paper dark:disabled:hover:bg-background-paperDark disabled:hover:border-divider dark:disabled:hover:border-divider-dark"
                >
                  <span>{itemsPerPage} per page</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isItemsPerPageOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Dropdown menu */}
                {isItemsPerPageOpen && (
                  <div className="absolute bottom-full left-0 mb-2 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-md shadow-lg z-50 min-w-[120px] overflow-hidden">
                    {[15, 25, 50, 100, 200].map((value) => (
                      <button
                        key={value}
                        onClick={() => {
                          handleLimitChange(value);
                          setIsItemsPerPageOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          itemsPerPage === value
                            ? "bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary font-medium"
                            : "text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                        }`}
                      >
                        {value} per page
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Right side - Page navigation */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                  {pagination.totalCount > 0
                    ? `Showing ${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, pagination.totalCount)} of ${pagination.totalCount}`
                    : "No customers"}
                </span>
                {pagination.totalCount > 0 && (
                  <>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!pagination.hasPreviousPage}
                      className="px-3 py-1.5 text-sm font-medium text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Prev</span>
                    </button>

                    {/* Page number buttons */}
                    <div className="flex items-center gap-1.5">
                      {Array.from(
                        { length: Math.min(5, pagination.totalPages) },
                        (_, i) => {
                          let pageNum;
                          if (pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= pagination.totalPages - 2) {
                            pageNum = pagination.totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`min-w-[32px] px-2 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                currentPage === pageNum
                                  ? "bg-secondary text-white dark:bg-secondary dark:text-white"
                                  : "text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        },
                      )}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="px-3 py-1.5 text-sm font-medium text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center gap-1"
                    >
                      <span>Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Customer Slide-Over Panel */}
      {isAddCustomerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md z-50"
            style={{
              opacity: isClosing ? 0 : isOpening ? 0 : 1,
              pointerEvents: isClosing ? "none" : "auto",
              transition: isClosing
                ? "opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
                : "opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
            }}
            onClick={handleClose}
          />

          {/* Slide-Over Panel */}
          <div
            ref={slideOverRef}
            className="fixed inset-y-0 right-0 w-full max-w-2xl bg-background-paper dark:bg-background-paperDark shadow-2xl z-50 overflow-y-auto"
            style={{
              transform: isOpening
                ? "translateX(100%)"
                : isClosing
                  ? "translateX(100%)"
                  : "translateX(0)",
              opacity: isClosing ? 0 : 1,
              transition: isClosing
                ? "transform 0.2s cubic-bezier(0.4, 0, 1, 1), opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
                : "transform 0.3s cubic-bezier(0, 0, 0.2, 1), opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
            }}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-divider dark:border-divider-dark">
                <div>
                  <h2 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark">
                    {showResults
                      ? "Import Results"
                      : "Import Customer from HubSpot"}
                  </h2>
                  <p className="text-sm text-text-secondary dark:text-text-secondaryDark mt-1">
                    {showResults
                      ? "Review the import results below"
                      : "Search and import HubSpot companies to create unified customer records"}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 px-6 py-6 overflow-y-auto">
                {!showResults ? (
                  <>
                    {/* Search */}
                    <div className="mb-4 flex-shrink-0">
                      <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                        Search HubSpot Companies
                      </label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                        <input
                          type="text"
                          value={customerSearch}
                          onChange={(e) => setCustomerSearch(e.target.value)}
                          placeholder="Search by company name or email (min 2 characters)..."
                          className="w-full pl-10 pr-4 py-2 bg-background-panel dark:bg-background-panelDark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition-colors"
                        />
                        {searchingCompanies && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <Loader2 className="w-4 h-4 animate-spin text-text-secondary dark:text-text-secondaryDark" />
                          </div>
                        )}
                      </div>
                      {customerSearch.length > 0 &&
                        customerSearch.length < 2 && (
                          <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                            Search term must be at least 2 characters
                          </p>
                        )}
                    </div>

                    {/* Company List */}
                    <div className="flex-1 flex flex-col min-h-0">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark">
                          Select Customer
                        </label>
                        <div className="flex items-center gap-2">
                          {!searchingCompanies &&
                            availableCompanies.length > 0 && (
                              <button
                                type="button"
                                onClick={handleSelectAllVisible}
                                className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark px-2 py-1 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors"
                              >
                                {allSelected ? "Deselect All" : "Select All"}
                              </button>
                            )}
                          {selectedCount > 0 && (
                            <span className="text-xs font-medium text-secondary dark:text-secondary px-2 py-1 bg-secondary/10 dark:bg-secondary/20 rounded-full">
                              {selectedCount} of{" "}
                              {Math.min(
                                availableCompanies.length,
                                MAX_SELECTIONS,
                              )}{" "}
                              selected
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto space-y-1.5">
                        {searchingCompanies ? (
                          <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin text-secondary mb-2" />
                            <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                              Searching companies...
                            </p>
                          </div>
                        ) : companies.length === 0 ? (
                          <div className="text-center py-8 text-text-secondary dark:text-text-secondaryDark">
                            {companiesConfigMissing ? (
                              <>
                                <p className="text-sm">
                                  Set your Contract Pipeline + Stage in Settings
                                  to add customers.
                                </p>
                              </>
                            ) : customerSearch.length >= 2 ? (
                              <>
                                <p className="text-sm">No companies found</p>
                                <p className="text-xs mt-1">
                                  Try adjusting your search
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-sm">
                                  No companies available
                                </p>
                                <p className="text-xs mt-1">
                                  Try searching for a company
                                </p>
                              </>
                            )}
                          </div>
                        ) : (
                          <>
                            {companies.map((company) => {
                              const isSelected = selectedCompanyIds.has(
                                company.id,
                              );
                              const isDisabled =
                                company.alreadyInSystem ||
                                (!isSelected &&
                                  selectedCompanyIds.size >= MAX_SELECTIONS);
                              return (
                                <label
                                  key={company.id}
                                  className={`w-full text-left p-3 rounded-lg border-2 transition-all cursor-pointer flex items-center gap-3 ${
                                    isSelected
                                      ? "border-secondary bg-secondary/10"
                                      : isDisabled
                                        ? "border-divider dark:border-divider-dark bg-background-panel dark:bg-background-panelDark opacity-50 cursor-not-allowed"
                                        : "border-divider dark:border-divider-dark bg-background-paper dark:bg-background-paperDark hover:border-divider dark:hover:border-divider-dark"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={isDisabled}
                                    onChange={() =>
                                      handleToggleCompany(company.id)
                                    }
                                    className="w-3.5 h-3.5 rounded border-divider dark:border-divider-dark text-secondary focus:ring-secondary focus:ring-offset-0 cursor-pointer flex-shrink-0 disabled:cursor-not-allowed"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <div className="text-xs font-medium text-text-primary dark:text-text-primaryDark truncate">
                                        {company.name}
                                      </div>
                                      {company.alreadyInSystem && (
                                        <span className="px-1.5 py-0.5 bg-green-light dark:bg-green-dark/20 text-green-dark dark:text-green-dark rounded text-[10px] font-medium">
                                          ✓ Already imported
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[10px] text-text-secondary dark:text-text-secondaryDark truncate mt-0.5">
                                      {company.domain
                                        ? `billing@${company.domain}`
                                        : "No email"}
                                    </div>
                                    <div className="text-[10px] font-mono text-text-secondary dark:text-text-secondaryDark truncate mt-0.5">
                                      HubSpot ID: {formatHubSpotId(company.id)}
                                    </div>
                                    {company.hasDeals && (
                                      <div className="text-[10px] text-text-secondary dark:text-text-secondaryDark mt-1">
                                        {company.totalDealsCount} deal
                                        {company.totalDealsCount !== 1
                                          ? "s"
                                          : ""}
                                        {company.closedWonDealsCount > 0 && (
                                          <span className="ml-1">
                                            • {company.closedWonDealsCount}{" "}
                                            closed won
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                            {/* Load More Button */}
                            {companiesPagination.hasNextPage && (
                              <div className="flex justify-center mt-4">
                                <button
                                  type="button"
                                  onClick={loadMoreCompanies}
                                  disabled={isLoadingMoreCompanies}
                                  className="px-4 py-2 bg-background-panel dark:bg-background-panelDark hover:bg-background-paper dark:hover:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg text-sm font-medium text-text-primary dark:text-text-primaryDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                  {isLoadingMoreCompanies ? (
                                    <>
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      Loading...
                                    </>
                                  ) : (
                                    "Load More"
                                  )}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {customerSearch.length > 0 &&
                        customerSearch.length < 2 && (
                          <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                            Search term must be at least 2 characters
                          </p>
                        )}
                    </div>

                    {/* Import Progress */}
                    {importProgress && (
                      <div className="mt-4 p-4 bg-background-panel dark:bg-background-panelDark rounded-lg border border-divider dark:border-divider-dark">
                        <div className="mb-2">
                          <div className="h-2 bg-background-paper dark:bg-background-paperDark rounded-full overflow-hidden">
                            <div
                              className="h-full bg-secondary transition-all duration-300"
                              style={{
                                width: `${
                                  (importProgress.current /
                                    importProgress.total) *
                                  100
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                        <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
                          Importing {importProgress.companyName}... (
                          {importProgress.current}/{importProgress.total})
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-divider dark:border-divider-dark">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleImport}
                        disabled={selectedCount === 0 || importing}
                        className="px-4 py-2 bg-black hover:bg-black/90 dark:bg-secondary dark:hover:bg-secondary-hover text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {importing ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            Import Selected
                            {selectedCount > 0 ? ` (${selectedCount})` : ""}
                          </>
                        )}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Results Summary */}
                    <div className="mb-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-light dark:bg-green-dark/20 text-green-dark dark:text-green-dark rounded-full">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-sm font-medium">
                            {importResults.filter((r) => r.success).length}{" "}
                            imported successfully
                          </span>
                        </div>
                        {importResults.filter((r) => !r.success).length > 0 && (
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 text-red-500 rounded-full">
                            <XCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              {importResults.filter((r) => !r.success).length}{" "}
                              failed
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Results List */}
                      <div className="space-y-3">
                        {importResults.map((result, index) => (
                          <div
                            key={index}
                            className={`p-4 rounded-lg border-2 ${
                              result.success
                                ? "border-green-500/20 bg-green-500/5 dark:bg-green-500/10"
                                : "border-red-500/20 bg-red-500/5 dark:bg-red-500/10"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              {result.success ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                              )}
                              <span className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                                {result.company.name}
                              </span>
                            </div>
                            {result.success && result.customer && (
                              <div className="space-y-1.5 text-xs text-text-secondary dark:text-text-secondaryDark">
                                <div>
                                  <strong>Global ID:</strong>{" "}
                                  {result.customer.globalId}
                                </div>
                                <div>
                                  <strong>Email:</strong>{" "}
                                  {result.customer.email}
                                </div>
                                {result.dealProcessing && (
                                  <div className="mt-2 pt-2 border-t border-divider dark:border-divider-dark">
                                    <strong className="text-text-primary dark:text-text-primaryDark">
                                      Deal Processing:
                                    </strong>
                                    <ul className="mt-1 space-y-0.5 ml-4 list-disc">
                                      <li>
                                        {result.dealProcessing.processed} deal
                                        {result.dealProcessing.processed !== 1
                                          ? "s"
                                          : ""}{" "}
                                        processed
                                      </li>
                                      <li>
                                        {result.dealProcessing.contracts.length}{" "}
                                        contract
                                        {result.dealProcessing.contracts
                                          .length !== 1
                                          ? "s"
                                          : ""}{" "}
                                        created
                                      </li>
                                      {result.dealProcessing.skipped > 0 && (
                                        <li>
                                          {result.dealProcessing.skipped} deal
                                          {result.dealProcessing.skipped !== 1
                                            ? "s"
                                            : ""}{" "}
                                          skipped (already had contracts)
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                            {!result.success && result.error && (
                              <div className="text-xs text-red-500 mt-1">
                                <strong>Error:</strong> {result.error}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-divider dark:border-divider-dark">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 bg-black hover:bg-black/90 dark:bg-secondary dark:hover:bg-secondary-hover text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        Done
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Customers;
